import { useEffect, useState } from "react";
import { C, withAlpha } from "../../constants/theme";
import { fbList } from "../../hooks/useFirebase";
import { useTaxasLojas } from "../../hooks/useTaxasLojas";
import { useVendaOnlineConfig } from "../../hooks/useVendaOnlineConfig";
import { listaMaquininhas } from "../../constants/maquininhas";
import { toast } from "../../hooks/useToast";
import { CANAL_BALCAO, TIPOS_CUSTO, listaCanais, novoCusto, lerConfig, itemDeEmbalagem } from "../../utils/vendaOnline";
import { ChipsCustos, EmbalagemCampos, CartaoCampos, campo, dica, rotulo } from "../pedidos/CamposVendaOnline";

/* Configurações → Vendas online: a lista de custos adicionais salvos e o
   padrão de cada loja (custos, embalagem e cartão que entram sozinhos quando
   o pedido é marcado como daquela loja). Tudo em users/{uid}.vendaOnline. */

const PADRAO_VAZIO = { custos:[], embalagem:[], maquininha_id:"", cartao:"", cartao_quem:"loja" };
const numero = (v) => Math.max(0, parseFloat(String(v ?? "").replace(",", ".")) || 0);

function Secao({ titulo, cor = C.accent, children }) {
  return (
    <section style={{ background:C.card, border:"1px solid "+withAlpha(cor, "33"), borderRadius:12, padding:14, display:"flex", flexDirection:"column", gap:12 }}>
      <h3 style={{ color:C.text, fontSize:15, fontWeight:700, margin:0 }}>{titulo}</h3>
      {children}
    </section>
  );
}

export function TelaVendasOnline({ uid }) {
  const { config, paymentConfig, carregado, salvar } = useVendaOnlineConfig(uid);
  const tabela = useTaxasLojas();
  const canais = listaCanais(tabela.lojas).filter(c => c.id !== CANAL_BALCAO);
  const maquininhas = listaMaquininhas(paymentConfig);
  const [materiais, setMateriais] = useState([]);
  const [rascunho, setRascunho] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { fbList("materiais").then(m => setMateriais(m || [])).catch(() => {}); }, []);
  useEffect(() => { if (carregado && rascunho === null) setRascunho(config); }, [carregado, config, rascunho]);

  if (!rascunho) return <div style={{ color:C.muted, padding:40, textAlign:"center" }}>⏳ Carregando...</div>;

  const catalogo = rascunho.custos.filter(c => String(c.nome || "").trim());
  const updCusto = (id, patch) => setRascunho(r => ({ ...r, custos:r.custos.map(c => c.id === id ? { ...c, ...patch } : c) }));
  const addCusto = () => setRascunho(r => ({ ...r, custos:[...r.custos, { ...novoCusto({ nome:"", tipo:"fixo" }), valor:"" }] }));
  const delCusto = (id) => setRascunho(r => ({
    ...r,
    custos:r.custos.filter(c => c.id !== id),
    canais:Object.fromEntries(Object.entries(r.canais).map(([k, p]) => [k, { ...p, custos:(p.custos || []).filter(x => x !== id) }])),
  }));
  const updPadrao = (canal, patch) => setRascunho(r => ({ ...r, canais:{ ...r.canais, [canal]:{ ...PADRAO_VAZIO, ...(r.canais[canal] || {}), ...patch } } }));
  const limparPadrao = (canal) => setRascunho(r => {
    const { [canal]:_removido, ...resto } = r.canais;
    return { ...r, canais:resto };
  });

  const salvarTudo = async () => {
    const custos = rascunho.custos
      .map(c => ({ id:c.id, nome:String(c.nome || "").trim(), tipo:c.tipo || "fixo", valor:numero(c.valor) }))
      .filter(c => c.nome);
    const ids = new Set(custos.map(c => c.id));
    const canaisLimpos = Object.fromEntries(Object.entries(rascunho.canais).map(([k, p]) => [k, {
      ...PADRAO_VAZIO, ...p,
      custos:(p.custos || []).filter(id => ids.has(id)),
      embalagem:(p.embalagem || []).map(e => ({ id:e.id, qtd:Math.max(1, numero(e.qtd) || 1) })),
    }]));
    const novo = lerConfig({ custos, canais:canaisLimpos });
    setSalvando(true);
    try {
      await salvar(novo);
      setRascunho(novo);
      toast("Configurações de venda online salvas!");
    } catch (e) {
      console.error(e);
      toast("Erro ao salvar. Tente novamente.", "error");
    }
    setSalvando(false);
  };

  return (
    <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ fontSize:12, color:C.muted, lineHeight:1.7, padding:"10px 12px", background:withAlpha(C.accent2, "0d"), border:"1px solid "+withAlpha(C.accent2, "33"), borderRadius:8 }}>
        <strong style={{ color:C.text }}>Prepare os próximos pedidos.</strong> Salve os custos, a embalagem e as preferências de cartão de cada canal. Eles serão preenchidos ao escolher o canal no pedido e podem ser ajustados em cada venda.
      </div>

      <Secao titulo="💸 Seus custos adicionais">
        <div style={dica}>Cadastre gastos como etiquetas, embalagens e impostos para selecionar nos pedidos. Custos em porcentagem usam o valor pago pelo cliente.</div>
        {rascunho.custos.map(c => (
          <div key={c.id} style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
            <input aria-label="Nome do custo" value={c.nome} onChange={e => updCusto(c.id, { nome:e.target.value })} placeholder="Ex: Etiqueta"
              style={{ ...campo, flex:"1 1 150px", width:"auto" }} />
            <select aria-label="Tipo do custo" value={c.tipo} onChange={e => updCusto(c.id, { tipo:e.target.value })} style={{ ...campo, flex:"0 1 140px", width:"auto" }}>
              {TIPOS_CUSTO.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <input aria-label="Valor do custo" type="number" min="0" step="0.01" inputMode="decimal" value={c.valor} onChange={e => updCusto(c.id, { valor:e.target.value })}
              placeholder={c.tipo === "pct" ? "%" : "R$"} style={{ ...campo, flex:"0 1 90px", width:"auto" }} />
            <button type="button" aria-label={`Apagar ${c.nome || "custo"}`} onClick={() => delCusto(c.id)}
              style={{ background:"none", border:"1px solid "+withAlpha(C.danger, "55"), color:C.danger, borderRadius:7, padding:"7px 10px", fontSize:12, cursor:"pointer" }}>✕</button>
          </div>
        ))}
        <button type="button" onClick={addCusto}
          style={{ width:"100%", background:withAlpha(C.accent, "12"), border:"1px dashed "+withAlpha(C.accent, "55"), color:C.accent, borderRadius:8, padding:"9px 0", fontSize:12, fontWeight:700, cursor:"pointer" }}>
          + Adicionar custo
        </button>
      </Secao>

      <Secao titulo="⭐ Preferências por canal" cor={C.accent2}>
        <div style={dica}>Abra um canal para definir os custos, a embalagem e as opções de cartão usados nos próximos pedidos.</div>
        {canais.map(c => {
          const padrao = rascunho.canais[c.id] || null;
          const p = { ...PADRAO_VAZIO, ...(padrao || {}) };
          const ativos = (p.custos || []).map(id => catalogo.find(k => k.id === id)).filter(Boolean);
          const embalagem = (p.embalagem || [])
            .map(e => { const m = materiais.find(x => x.id === e.id); return m ? { ...itemDeEmbalagem(m, 1), qtd:e.qtd } : null; })
            .filter(Boolean);
          return (
            <details key={c.id} open={!!padrao} aria-label={`Padrão ${c.nome}`} style={{ background:C.panel, border:"1px solid "+C.border, borderRadius:10, padding:"10px 12px" }}>
              <summary style={{ cursor:"pointer", display:"flex", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between", gap:8, minHeight:40, fontSize:13, fontWeight:700, color:C.text }}>
                <span>{c.icone} {c.nome}</span>
                <span style={{ fontSize:10.5, fontWeight:700, color:padrao ? C.success : C.muted }}>{padrao ? "● com padrão" : "sem padrão"}</span>
              </summary>
              <div style={{ display:"flex", flexDirection:"column", gap:12, marginTop:12 }}>
                <div>
                  <span style={rotulo}>CUSTOS ADICIONAIS</span>
                  {catalogo.length
                    ? <ChipsCustos catalogo={catalogo} ativos={ativos} onChange={lista => updPadrao(c.id, { custos:lista.map(x => x.id) })} />
                    : <div style={dica}>Adicione custos na seção acima para selecioná-los aqui.</div>}
                </div>
                <div>
                  <span style={rotulo}>EMBALAGEM E ENVIO</span>
                  <EmbalagemCampos materiais={materiais} itens={embalagem} onChange={itens => updPadrao(c.id, { embalagem:itens.map(i => ({ id:i.id, qtd:i.qtd })) })} />
                </div>
                <div>
                  <span style={rotulo}>CARTÃO</span>
                  <CartaoCampos valor={p} maquininhas={maquininhas} marketplace={!!c.marketplace} mostrarPct={false}
                    onChange={({ cartao_pct:_pct, ...patch }) => updPadrao(c.id, patch)} />
                </div>
                {padrao && (
                  <button type="button" onClick={() => limparPadrao(c.id)}
                    style={{ alignSelf:"flex-start", background:"none", border:"none", color:C.danger, fontSize:11.5, fontWeight:700, cursor:"pointer", padding:0 }}>
                    Limpar padrão de {c.nome}
                  </button>
                )}
              </div>
            </details>
          );
        })}
      </Secao>

      <button type="button" onClick={salvarTudo} disabled={salvando}
        style={{ width:"100%", padding:11, background:"linear-gradient(135deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", border:"none", borderRadius:9, color:"var(--pc-ui-04111f, #04111f)", fontSize:12, fontWeight:800, cursor:"pointer", opacity:salvando ? 0.6 : 1 }}>
        {salvando ? "⏳ Salvando..." : "💾 Salvar configurações"}
      </button>
    </div>
  );
}
